import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/** FastAPI may return detail as string or validation array */
function formatCheckoutError(err) {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((item) => (item && typeof item === 'object' && item.msg ? item.msg : JSON.stringify(item))).join(' ');
  }
  if (!err.response && err.message) return `${err.message} (check REACT_APP_API_URL / network)`;
  return 'Could not start checkout. Please try again.';
}

const PLANS_MONTHLY = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Get started with paper search',
    featured: false,
    features: [
      '5 searches per day (PubMed, arXiv, OpenAlex, Europe PMC, bioRxiv)',
      '3 free protocol generations (lifetime)',
      'Knowledge graph',
      'Save papers to workspace',
      'Export workspace as CSV',
      'Clinical trial finder',
      'Zotero sync & reference formatter',
    ],
    locked: [
      'Unlimited searches',
      'Protocol generation (unlimited)',
      'Design Assistant',
      'Search summarization',
      'Hypothesis generation',
      'Grant Assistant',
      'Literature Review Generator',
    ],
    cta: 'Get Started Free',
    note: null,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$15',
    period: '/month',
    description: 'For individual researchers',
    featured: true,
    features: [
      'Unlimited searches',
      '50 AI credits/month (~25 protocol generations)',
      'Protocol generation & Design Assistant',
      'Hypothesis generation',
      'Grant Assistant & Literature Reviews',
      'Similarity analysis & search summaries',
      'PDF + DOCX export',
      'Protocol version history',
      'Priority email support',
    ],
    locked: [],
    cta: 'Upgrade to Pro',
    note: null,
  },
  {
    id: 'lab',
    name: 'Lab',
    price: '$49',
    period: '/month',
    description: 'For labs and research teams',
    featured: false,
    features: [
      'Everything in Pro',
      '5 seats (shared credit pool)',
      '200 AI credits/month (shared pool)',
      'Shared protocol library',
      'Lab manager usage dashboard',
      'Priority support + onboarding call',
    ],
    locked: [],
    cta: 'Upgrade to Lab',
    note: null,
  },
];

const PLANS_ANNUAL = [
  {
    ...PLANS_MONTHLY[0],
  },
  {
    ...PLANS_MONTHLY[1],
    id: 'pro_annual',
    price: '$12',
    period: '/month',
    description: 'For individual researchers — billed $144/yr',
    cta: 'Upgrade to Pro (Annual)',
  },
  {
    ...PLANS_MONTHLY[2],
    id: 'lab_annual',
    price: '$39',
    period: '/month',
    description: 'For labs and research teams — billed $468/yr',
    cta: 'Upgrade to Lab (Annual)',
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState('monthly');

  useEffect(() => {
    document.title = 'Pricing — Protovera';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (el) el.setAttribute('content', content);
    };
    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (el) el.setAttribute('href', href);
    };
    setMeta('description', 'Protovera pricing: Free plan with 5 searches/day and 3 protocol generations. Pro at $15/month for unlimited searches and 50 AI credits. Lab at $49/month for research teams.');
    setCanonical(`${window.location.origin}/pricing`);
    return () => {
      document.title = 'Protovera — AI Research Protocol Generator for Life Sciences';
      setCanonical(`${window.location.origin}/`);
    };
  }, []);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState('');
  /** null = not loaded yet; object from GET /api/payments/config */
  const [payConfig, setPayConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/payments/config')
      .then((res) => {
        if (!cancelled) setPayConfig(res.data);
      })
      .catch(() => {
        if (!cancelled) setPayConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const plans = billing === 'annual' ? PLANS_ANNUAL : PLANS_MONTHLY;

  /** Paid plan has Stripe key + price ID on server (from config). Unknown until loaded — allow click. */
  const isCheckoutReadyForPlan = (planId) => {
    if (planId === 'free') return true;
    if (!payConfig || payConfig.plans == null) return true;
    return payConfig.plans[planId] === true;
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free') { navigate('/register'); return; }
    if (!user) { navigate('/login'); return; }
    setLoadingPlan(planId);
    setError('');
    try {
      const res = await api.post(`/api/payments/create-checkout-session?plan=${planId}`, {});
      window.location.href = res.data.url;
    } catch (err) {
      setError(formatCheckoutError(err));
    } finally {
      setLoadingPlan(null);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const currentPlanId = user?.plan === 'pro' ? (billing === 'annual' ? 'pro_annual' : 'pro')
    : user?.plan === 'lab' ? (billing === 'annual' ? 'lab_annual' : 'lab')
    : user?.plan || 'free';

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="bg-white" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-base font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>
            Protovera
          </Link>
          <div className="flex gap-3">
            {user ? (
              <Link to="/app" className="text-sm text-[#4d4d4d] font-medium hover:text-[#171717]">Go to App →</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-[#4d4d4d] hover:text-[#171717]">Sign In</Link>
                <Link
                  to="/register"
                  className="text-sm bg-[#171717] text-white px-4 py-1.5 rounded-md font-medium hover:bg-[#2e2e2e] transition-colors"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-semibold text-[#171717]" style={{ letterSpacing: '-2.4px' }}>
            Pricing for Protovera
          </h1>
          <p className="text-[#808080] mt-3 text-base">
            Start with literature search, then upgrade for grounded AI drafting and analysis.
          </p>
          {user && (
            <p className="mt-3 text-sm text-[#4d4d4d] font-medium">
              You're on the <span className="capitalize text-[#171717]">{user.plan}</span> plan.
            </p>
          )}
          {user && user.ai_heavy_usage_limit > 0 && (
            <p className="mt-1 text-xs text-[#808080]">
              AI credits this month: {user.ai_heavy_usage_count} / {user.ai_heavy_usage_limit}
            </p>
          )}

          {/* Billing toggle */}
          <div className="mt-6 inline-flex items-center rounded-lg p-1 gap-1" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px', background: 'white' }}>
            <button
              onClick={() => setBilling('monthly')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                billing === 'monthly'
                  ? 'bg-[#171717] text-white'
                  : 'text-[#4d4d4d] hover:text-[#171717]'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                billing === 'annual'
                  ? 'bg-[#171717] text-white'
                  : 'text-[#4d4d4d] hover:text-[#171717]'
              }`}
            >
              Annual
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                billing === 'annual' ? 'bg-white text-[#171717]' : 'bg-[#171717] text-white'
              }`}>
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {payConfig && !payConfig.checkout_available && user?.is_admin && (
          <div className="max-w-2xl mx-auto mb-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 text-center">
            [Admin] Online checkout is not enabled. Add{' '}
            <code className="text-xs bg-amber-100 px-1 rounded">STRIPE_SECRET_KEY</code> and the{' '}
            <code className="text-xs bg-amber-100 px-1 rounded">STRIPE_*_PRICE_ID</code> variables on the API host; see{' '}
            <code className="text-xs bg-amber-100 px-1 rounded">backend/.env.example</code>.
          </div>
        )}

        {error && (
          <div className="max-w-md mx-auto mb-8 bg-red-50 rounded-lg px-4 py-3 text-sm text-red-700 text-center">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-xl p-8 relative"
              style={{
                boxShadow: plan.featured
                  ? 'rgba(0,0,0,0.12) 0px 0px 0px 2px, rgba(0,0,0,0.04) 0px 4px 12px'
                  : 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
              }}
            >
              {plan.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#171717] text-white text-xs font-medium px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}

              <div className="mb-6">
                <h2 className="text-base font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>
                  {plan.name}
                </h2>
                <div className="mt-2 flex items-end gap-1">
                  <span className="text-4xl font-semibold text-[#171717]" style={{ letterSpacing: '-2.4px' }}>
                    {plan.price}
                  </span>
                  <span className="text-[#808080] text-sm mb-1">{plan.period}</span>
                </div>
                <p className="text-sm text-[#808080] mt-1">{plan.description}</p>
              </div>

              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={
                  loadingPlan === plan.id
                  || !isCheckoutReadyForPlan(plan.id)
                  || (user && (user.plan === 'pro' || user.plan === 'lab') && (plan.id === 'pro' || plan.id === 'pro_annual') && user.plan === 'pro')
                  || (user && user.plan === plan.id)
                }
                title={!isCheckoutReadyForPlan(plan.id) ? 'Checkout not configured for this plan on the server' : undefined}
                className="w-full py-2.5 rounded-md font-medium text-sm transition-colors disabled:opacity-50 bg-[#171717] text-white hover:bg-[#2e2e2e]"
              >
                {loadingPlan === plan.id
                  ? 'Redirecting...'
                  : !isCheckoutReadyForPlan(plan.id)
                  ? 'Unavailable'
                  : user && user.plan === 'pro' && (plan.id === 'pro' || plan.id === 'pro_annual')
                  ? 'Current Plan'
                  : user && user.plan === 'lab' && (plan.id === 'lab' || plan.id === 'lab_annual')
                  ? 'Current Plan'
                  : user && user.plan === plan.id
                  ? 'Current Plan'
                  : plan.cta}
              </button>

              <ul className="mt-6 space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4d4d4d]">
                    <span className="text-[#171717] mt-0.5 flex-shrink-0 font-semibold">✓</span>
                    {f}
                  </li>
                ))}
                {plan.locked.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#c0c0c0] line-through">
                    <span className="mt-0.5 flex-shrink-0 text-[#d4d4d4]">✕</span>
                    {f}
                  </li>
                ))}
              </ul>

              {plan.note && (
                <p className="mt-4 text-xs text-[#808080] border-t border-[#ebebeb] pt-3">
                  {plan.note}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center space-y-2">
          <p className="text-sm text-[#808080]">
            No credit card required for the Free plan. AI credits reset on the 1st of each month.
          </p>
          <p className="text-sm text-[#808080]">
            Working in a biotech or CRO?{' '}
            <a href="mailto:info@protovera.org" className="text-[#4d4d4d] hover:text-[#171717] underline">
              Contact us
            </a>
            {' '}for team pricing.
          </p>
        </div>
      </main>
    </div>
  );
}
